import { useMemo } from 'react';
import { useInfo } from '../../../Flow/ConsentContextProvider';
import type { BitbucketWorkspace } from '#definitions/consent-info';
import { buildSiteOrgIndex, isOrgMismatch, resolveSiteOrgId } from './orgMatch';

export interface UseOrgMatchArgs {
  /**
   * Whether the two pickers can disagree at all: both are in play and this flow
   * compares them. False short-circuits to "no mismatch".
   */
  applies: boolean;
  /** The selected Atlassian site URL, as held by consent context. */
  selectedWorkspace: string | null | undefined;
  /** The selected Bitbucket workspace, which carries its own orgId. */
  selectedBitbucketWorkspace: BitbucketWorkspace | null;
}

export interface OrgMatch {
  /** Organization owning the selected site, or null when unknown. */
  siteOrgId: string | null;
  /** Organization owning the selected Bitbucket workspace, or null when unknown. */
  bitbucketOrgId: string | null;
  /** Both orgs are known and differ, so the selections cannot be granted together. */
  hasOrgMismatch: boolean;
}

/**
 * Keeps the site and Bitbucket selections within one organization.
 *
 * A consent grants a single organization's data, so a site from one org and a
 * Bitbucket workspace from another cannot be granted together. Both selectors
 * report the conflict and Accept is blocked until one of them changes.
 *
 * Applies only when both pickers are in play. A flow that shows sites alone has
 * nothing to disagree with, and Trello is unaffected — it is never offered
 * alongside Bitbucket and carries no orgId to compare.
 */
export function useOrgMatch({
  applies,
  selectedWorkspace,
  selectedBitbucketWorkspace,
}: UseOrgMatchArgs): OrgMatch {
  const info = useInfo();

  const siteOrgIndex = useMemo(
    () => buildSiteOrgIndex(info),
    [info.availableOrganizations],
  );

  return useMemo(() => {
    if (!applies) {
      return { siteOrgId: null, bitbucketOrgId: null, hasOrgMismatch: false };
    }
    const siteOrgId = resolveSiteOrgId(siteOrgIndex, selectedWorkspace);
    const bitbucketOrgId = selectedBitbucketWorkspace?.orgId ?? null;
    return {
      siteOrgId,
      bitbucketOrgId,
      hasOrgMismatch: isOrgMismatch(siteOrgId, bitbucketOrgId),
    };
  }, [applies, siteOrgIndex, selectedWorkspace, selectedBitbucketWorkspace]);
}
